import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, FormArray, Validators, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import flatpickr from 'flatpickr';
import { Italian } from 'flatpickr/dist/l10n/it';
import { Instance as FlatpickrInstance } from 'flatpickr/dist/types/instance';
import { TourGuideService } from '../../services/tour-guide.service';
import { TourGuideComponent } from '../../components/tour-guide/tour-guide.component';
import { RETURN_REQUEST_TOUR_CONFIG } from '../../configs/return-request-tour.config';
import { AuthService } from '../../services/auth.service';
import { NotificationService } from '../../services/notification.service';
import { User } from '../../interfaces/user.interfaces';
import { ReturnRequestService, ClientBrandsEntry, ReturnRequestFilters } from '../../services/return-request.service';
import { SysReturnList } from '../../interfaces/SysReturnList.interfaces';
import { Dealer } from '../../interfaces/waybill.interfaces';
import { Configuration } from '../../interfaces/Configuration.interfaces';
import { Pallets } from '../../interfaces/Pallets.interfaces';
import { ReturnSettingsPopupComponent } from './return-settings-popup/return-settings-popup.component';
import { ReturnRequestPrintPopupComponent } from './return-request-print-popup/return-request-print-popup.component';
import { ReturnRequestSummaryPopupComponent } from './return-request-summary-popup/return-request-summary-popup.component';
import { of, Subscription } from 'rxjs';
import { catchError } from 'rxjs/operators';


interface Company {
  companyId: number;
  code: string;
  name: string;
  vatNumber: string;
  city: string;
  address: string;
}

interface ReturnRequestFormFieldConfig {
  name: string;
  label: string;
  type: 'text' | 'number' | 'date' | 'select' | 'textarea';
  required?: boolean;
  managedBy?: 'user' | 'frontend' | 'system' | 'backend';
  autoValueFrom?: 'selectedCompanyCode' | 'selectedCompanyName' | 'selectedCompanyCity' | 'selectedCompanyAddress' | 'todayDate';
  placeholder?: string;
  icon?: string;
  colClass?: string;
  optionsSource?: 'companies' | 'deliveryTypes' | 'palletStatuses' | 'warehouses' | 'carriers' | 'returnList' | 'returnNames';
  defaultValue?: string;
  /** Limiti per i campi numerici (validazione + attributi min/max dell'input). */
  min?: number;
  max?: number;
  /** Numero massimo di decimali ammessi per i campi numerici (es. 3 = 0.001). */
  decimals?: number;
  /** Lunghezza massima per i campi testuali (attributo maxlength dell'input). */
  maxLength?: number;
  /**
   * Ambito del campo:
   *  - 'general' (default): valore unico condiviso da tutti i pallet della richiesta
   *  - 'item'             : valore specifico per ogni pallet (es. codici di abbinamento)
   * In modalità 'generate' i campi 'item' vengono nascosti perché generati lato server.
   */
  scope?: 'general' | 'item';
}

interface ReturnRequestFormConfig {
  version: string;
  title: string;
  /**
   * Modalità del form derivata dalla configurazione del dealer:
   *  - 'insert'  : il frontend crea N entità Pallets (1..99) e le invia in batch al backend
   *  - 'generate': il frontend invia un singolo request al backend che genera N entità Pallets server-side
   */
  form?: 'insert' | 'generate';
  fields: ReturnRequestFormFieldConfig[];
  submission: {
    multiCreateBy: 'single' | 'dealerConfigurations';
    mapFormToPallet: Record<string, keyof Pallets>;
  };
}

/**
 * Modello riga UI richiesta di reso.
 * Estende Pallets per usare il modello DTO condiviso nel form "Nuova richiesta di reso".
 */
interface ReturnRequest extends Pallets {
  id: number;
  companyCode: string;
  companyName: string;
  palletCode: string;
  palletDeliveryType: string;
  palletDealerCode: string;
  dealerName: string;
  dealerOperatingAddressStreet: string;
  dealerOperatingAddressCity: string;
  palletStatus: string;
  warehouseCode: string;
  carrierCode: string;
  notes: string;
  createdAt: string;
}

@Component({
  selector: 'app-return-request',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    TranslateModule,
    TourGuideComponent,
    ReturnSettingsPopupComponent,
    ReturnRequestPrintPopupComponent,
    ReturnRequestSummaryPopupComponent
  ],
  templateUrl: './return-request.component.html',
  styleUrl: './return-request.component.css'
})
export class ReturnRequestComponent implements OnInit, OnDestroy {

  // ===== SECTION 1: User & Companies =====
  user: User | null = null;

  companies: Company[] = [];
  /**
   * False quando la configurazione dell'utente (opr_configuration ->
   * userFunctions.dealers) non contiene alcun dealer: la pagina mostra solo
   * l'avviso di funzione non abilitata.
   */
  hasDealerCodes: boolean = true;
  dealersByCode: Record<string, Dealer> = {};
  dealerConfigurationsByCode: Record<string, Configuration[]> = {};
  selectedDealerConfigurations: Configuration[] = [];

  selectedCompanyCode: string = '';
  /** True se il dealer selezionato ha una configurazione form valida (oprConfiguration.configurationValue). */
  selectedDealerHasConfig: boolean = false;
  /** Codici delle card azienda con il dettaglio (P. IVA, sede) espanso. */
  expandedCompanyCodes: string[] = [];

  // ===== SECTION 2: Return request form =====
  returnForm: FormGroup;
  formConfig: ReturnRequestFormConfig | null = null;
  /** Tutti i campi della configurazione corrente. */
  formFields: ReturnRequestFormFieldConfig[] = [];
  /** Campi a livello richiesta (uno per submit). */
  generalFields: ReturnRequestFormFieldConfig[] = [];
  /** Campi specifici di ogni pallet (replicati per ogni elemento del FormArray). */
  itemFields: ReturnRequestFormFieldConfig[] = [];
  /**
   * Modalità corrente del form di reso, ricavata dalla configurazione del dealer selezionato.
   * Default 'insert' (creazione client-side fino a 99 pallet).
   */
  formMode: 'insert' | 'generate' = 'insert';
  /** Quantità pallet richiesta (solo modalità 'generate'). In 'insert' usiamo la lunghezza del FormArray. */
  palletQuantity: number = 1;
  readonly maxPalletQuantity: number = 99;
  /** Indice del pallet espanso nel FormArray (per UI compatta). */
  collapsedPalletStates: Record<number, boolean> = {};
  isUserSectionCollapsed = false;
  isFormCollapsed = false;
  /** Sottoscrizione al campo 'Azienda' del form, ricreata a ogni rebuild del form. */
  private companyCodeChangesSub?: Subscription;
  /** Sottoscrizione al campo 'Tipologia reso' generale (cascading verso Tipologia Contenitore). */
  private returnNameChangesSub?: Subscription;

  deliveryTypes: { code: string; name: string }[] = [];

  /** Record SYS_RETURN_LIST raw per il dealer selezionato (per il cascading tipologia -> contenitore). */
  private sysReturnList: SysReturnList[] = [];
  /** Tipologie reso: distinct di returnName da SYS_RETURN_LIST. */
  returnNameOptions: { code: string; name: string }[] = [];

  palletStatuses: { code: string; name: string }[] = [];

  warehouses: { code: string; name: string }[] = [];

  carriers: { code: string; name: string }[] = [];

  // Entita tipizzate con la nuova interfaccia create dalla sezione "Nuova richiesta di reso"
  createdPallets: Pallets[] = [];

  // ===== SECTION 3: Existing requests list =====
  returnRequests: ReturnRequest[] = [];
  filteredRequests: ReturnRequest[] = [];
  globalSearchTerm: string = '';
  filterByCompany: string = '';
  currentPage: number = 1;
  itemsPerPage: number = 10;
  /** Totale record lato backend (paginazione server-side). */
  totalCount: number = 0;
  /** Parametri dell'ultima ricerca pallet, riusati per i cambi pagina server-side. */
  private currentDealerCodes: string[] = [];
  private currentClientBrands?: ClientBrandsEntry[];
  readonly pageSizeOptions: number[] = [5, 10, 25, 50, 100];
  private readonly pageSizeStorageKey = 'return-request.items-per-page';

  // ===== SECTION 3: Filtri e Azioni =====
  isFiltersCollapsed = false;
  isAdvancedFiltersCollapsed = true;
  filterPalletCode: string = '';
  filterWaybillCode: string = '';
  filterShipmentCode: string = '';
  filterDealerCode: string = '';
  filterDealerName: string = '';
  filterDealerCity: string = '';
  filterStatus: string = '';
  filterDeliveryType: string = '';
  filterWarehouse: string = '';
  filterCarrier: string = '';

  // Filtri data: stesso comportamento di shipment-management (flatpickr con
  // ora fissa 00:00 per "Data Da" e 23:59 per "Data A", più i pulsanti di
  // navigazione giorno precedente / oggi / giorno successivo / cancella).
  @ViewChild('filterDateFromInput', { static: false }) filterDateFromInput!: ElementRef<HTMLInputElement>;
  @ViewChild('filterDateToInput', { static: false }) filterDateToInput!: ElementRef<HTMLInputElement>;
  selectedFilterDateFrom: Date | null = null;
  selectedFilterDateTo: Date | null = null;
  private filterDateFromFlatpickrInstance: FlatpickrInstance | null = null;
  private filterDateToFlatpickrInstance: FlatpickrInstance | null = null;

  // Expose Math to template (same pattern used in shipment-management)
  Math = Math;
  isRequestsLoading = false;

  // ===== Return Settings popup (admin only) =====
  isReturnSettingsPopupVisible: boolean = false;

  // ===== Print popup (stampa label / bolla del reso) =====
  isPrintPopupVisible: boolean = false;
  printPopupRequest: ReturnRequest | null = null;
  /** Anagrafica del dealer della riga selezionata (per i dati della label). */
  printPopupDealer: Dealer | null = null;
  /** Resi della stessa bolla del reso selezionato, elencati nel popup. */
  printPopupPallets: Pallets[] = [];

  // ===== Summary popup (riepilogo dopo la creazione dei resi) =====
  isSummaryPopupVisible: boolean = false;
  /** Pallet restituiti dal backend alla creazione: elenco resi + bolle. */
  summaryPopupPallets: Pallets[] = [];
  /** Anagrafica del dealer dei resi appena creati (per i dati delle label). */
  summaryPopupDealer: Dealer | null = null;

  constructor(private fb: FormBuilder,
              private tourGuideService: TourGuideService,
              private authService: AuthService,
              private returnRequestService: ReturnRequestService,
              private notificationService: NotificationService,
              private translate: TranslateService) {
    this.returnForm = this.fb.group({});
  }

  ngOnInit(): void {
    this.applyFormConfig(this.getStaticFormConfig());
    this.loadItemsPerPagePreference();
    this.loadCurrentUser();
    this.loadCompaniesFromDealers();
    this.applyFilters();
    // Expose component on window for tour-guide beforeShow callbacks
    (window as any).returnRequestComponent = this;
  }

  ngOnDestroy(): void {
    this.filterDateFromFlatpickrInstance?.destroy();
    this.filterDateToFlatpickrInstance?.destroy();
    this.companyCodeChangesSub?.unsubscribe();
    this.returnNameChangesSub?.unsubscribe();
  }

  /**
   * Carica i dati dell'utente corrente da AuthService (localStorage / token)
   */
  private loadCurrentUser(): void {
    const info = this.authService.getUserInfo();
    if (info) {
      this.user = info as User;
    }
  }

  /**
   * Estrae i client+brand da userFunctions (JSON string).
   * In userFunctions la lista sta sotto la chiave `clients`
   * (stessa fonte usata da shipment-management); `clientBrands`
   * è mantenuto come fallback per compatibilità.
   */
  private extractClientBrands(): any {
    const parsed = this.parseUserFunctions();
    return parsed?.clients ?? parsed?.clientBrands;
  }

  /**
   * Estrae eventuali configurazioni da userFunctions in forma
   * `{ [dealerCode]: Configuration[] }`.
   */
  private extractDealerConfigurationsFromUserFunctions(): Record<string, Configuration[]> {
    const parsed = this.parseUserFunctions();
    const raw = parsed?.configurations;
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
      return {};
    }

    const result: Record<string, Configuration[]> = {};
    for (const [dealerCode, value] of Object.entries(raw as Record<string, unknown>)) {
      if (!Array.isArray(value)) {
        continue;
      }

      const normalized = value
        .map((item: unknown) => this.normalizeConfiguration(item))
        .filter((item: Configuration | null): item is Configuration => item !== null);

      if (normalized.length) {
        result[dealerCode] = normalized;
      }
    }

    return result;
  }

  /**
   * Parse sicuro di userFunctions (puo essere stringa JSON oppure oggetto).
   */
  private parseUserFunctions(): any {
    try {
      const raw = this.user?.userFunctions;
      if (!raw) {
        return undefined;
      }
      return typeof raw === 'string' ? JSON.parse(raw) : raw;
    } catch {
      return undefined;
    }
  }

  /**
   * Estrae i dealerCodes dalle userFunctions dell'utente e carica
   * le aziende rappresentate tramite /return-request/getDealersByCodes.
   */
  private loadCompaniesFromDealers(): void {
    const dealerCodes = this.extractDealerCodes();
    const clientBrands = this.extractClientBrands() as ClientBrandsEntry[] | undefined;
    const userFunctionConfigurations = this.extractDealerConfigurationsFromUserFunctions();
    //console.log('Dealer codes da userFunctions:', dealerCodes);
    if (!dealerCodes.length) {
      // Nessun dealer nella configurazione dell'utente: la pagina non è
      // utilizzabile, al posto delle sezioni viene mostrato l'avviso.
      this.hasDealerCodes = false;
      this.companies = [];
      this.dealerConfigurationsByCode = {};
      this.selectedDealerConfigurations = [];
      this.returnRequests = [];
      this.applyFilters();
      //console.log('Dealers recuperati: []');
      return;
    }

    this.hasDealerCodes = true;

    this.loadPalletsForTable(dealerCodes, clientBrands);

    this.returnRequestService.getDealersByCodes(dealerCodes, clientBrands).subscribe({
      next: (dealers: Dealer[]) => {
        const list = dealers || [];
        this.companies = list.map(d => this.mapDealerToCompany(d));
        this.dealersByCode = list.reduce((acc, d) => {
          if (d.dealerCode) {
            acc[d.dealerCode] = d;
          }
          return acc;
        }, {} as Record<string, Dealer>);
        this.dealerConfigurationsByCode = this.buildDealerConfigurationsMap(list, userFunctionConfigurations);

        // Stampa a console i configurationValue della configurazione di ogni dealer
        // list.forEach((d) => {
        //   console.log(
        //     `[ReturnRequest] dealer=${d.dealerCode} configurationValue=`,
        //     d.oprConfiguration?.configurationValue
        //   );
        // });

        // Auto-seleziona l'unico dealer disponibile (UX: nessuna scelta da fare).
        if (this.companies.length === 1 && !this.selectedCompanyCode) {
          this.selectCompany(this.companies[0].code);
        }

        this.refreshMappedRequestsDetails();
      },
      error: (err) => {
        console.error('Errore caricamento dealers:', err);
        this.companies = [];
        this.dealersByCode = {};
        this.dealerConfigurationsByCode = {};
        this.selectedDealerConfigurations = [];
        this.refreshMappedRequestsDetails();
      }
    });
  }

  private loadPalletsForTable(dealerCodes: string[], clientBrands?: ClientBrandsEntry[]): void {
    this.currentDealerCodes = dealerCodes;
    this.currentClientBrands = clientBrands;
    this.currentPage = 1;
    this.fetchPalletsPage();
  }

  /**
   * Filtri della sezione "Filtri e Azioni" da inviare al backend: solo quelli
   * valorizzati, con le date in ISO 8601 (inizio/fine giornata già impostati
   * dai picker). Undefined se nessun filtro è attivo.
   */
  private buildRequestFilters(): ReturnRequestFilters | undefined {
    const filters: ReturnRequestFilters = {};
    const palletCode = this.filterPalletCode.trim();
    const waybillCode = this.filterWaybillCode.trim();

    if (palletCode) {
      filters.palletCode = palletCode;
    }
    if (waybillCode) {
      filters.palletWaybillCode = waybillCode;
    }
    if (this.selectedFilterDateFrom) {
      filters.dateFrom = this.selectedFilterDateFrom.toISOString();
    }
    if (this.selectedFilterDateTo) {
      filters.dateTo = this.selectedFilterDateTo.toISOString();
    }

    return Object.keys(filters).length ? filters : undefined;
  }

  /**
   * Scarica dal backend la sola pagina corrente (paginazione server-side):
   * ogni cambio di pagina o di elementi per pagina rifà la richiesta con
   * page e pageSize aggiornati, insieme ai filtri attivi. Il filtro azienda
   * restringe i dealerCodes inviati al backend a quello selezionato.
   */
  private fetchPalletsPage(): void {
    const dealerCodes = this.filterByCompany
      ? [this.filterByCompany]
      : this.currentDealerCodes;

    if (!dealerCodes.length) {
      this.returnRequests = [];
      this.totalCount = 0;
      this.isRequestsLoading = false;
      this.applyFilters();
      return;
    }

    this.isRequestsLoading = true;
    this.returnRequestService.getPalletsByDealerCodes(
      dealerCodes,
      this.currentClientBrands,
      this.currentPage,
      this.itemsPerPage,
      this.buildRequestFilters()
    ).pipe(
      catchError((err) => {
        console.error(`Errore caricamento pallet (pagina ${this.currentPage}):`, err);
        return of({ data: [], totalPages: 0, totalCount: 0, page: this.currentPage, pageSize: this.itemsPerPage });
      })
    ).subscribe((res) => {
      this.totalCount = Number(res?.totalCount || 0);
      this.returnRequests = (res?.data || []).map((pallet, index) => this.mapPalletToReturnRequest(pallet, index));
      this.isRequestsLoading = false;
      this.applyFilters();
    });
  }

  private mapPalletToReturnRequest(pallet: Pallets, fallbackIndex: number): ReturnRequest {
    const dealerCode = (pallet.palletDealerCode || '').trim();
    const companyCode = (pallet.palletClientCode || dealerCode).trim();
    const company = this.companies.find((c) => c.code === companyCode);
    const dealer = this.dealersByCode[dealerCode];

    // Lo spread va PRIMA dei campi calcolati: altrimenti i valori raw del
    // pallet (o quelli vuoti di una mappatura precedente) sovrascrivono
    // dealerName & co. quando refreshMappedRequestsDetails rimappa le righe.
    return {
      ...pallet,
      id: pallet.palletId ?? Date.now() + fallbackIndex,
      companyCode,
      companyName: company?.name || dealer?.dealerName || dealer?.dealerLegalName || companyCode,
      palletCode: pallet.palletCode || '',
      palletWaybillCode: pallet.palletWaybillCode || '',
      palletShipmentCode: pallet.palletShipmentCode || '',
      palletDate: pallet.palletDate || '',
      palletDeliveryType: pallet.palletDeliveryType || '',
      palletDealerCode: dealerCode,
      dealerName: dealer?.dealerName || dealer?.dealerLegalName || '',
      dealerOperatingAddressStreet: dealer?.dealerOperatingAddressStreet || dealer?.dealerLegalAddressStreet || '',
      dealerOperatingAddressCity: dealer?.dealerOperatingAddressCity || dealer?.dealerLegalAddressCity || '',
      palletStatus: pallet.palletStatus || 'REQUESTED',
      warehouseCode: pallet.palletDestinationLocation || '',
      carrierCode: pallet.palletCarrierCode || '',
      notes: pallet.palletNote || '',
      createdAt: pallet.palletCreatedAt || ''
    };
  }

  private refreshMappedRequestsDetails(): void {
    if (!this.returnRequests.length) {
      return;
    }

    this.returnRequests = this.returnRequests.map((request, index) => this.mapPalletToReturnRequest(request, index));
    this.applyFilters();
  }

  /**
   * Legge userFunctions (JSON string) dall'utente e ritorna l'array `dealers` come stringhe.
   */
  private extractDealerCodes(): string[] {
    try {
      const raw = this.user?.userFunctions;
      //console.log('userFunctions raw:', raw);
      if (!raw) {
        return [];
      }
      const parsed = this.parseUserFunctions();
      const dealers = parsed?.dealers;
      if (Array.isArray(dealers)) {
        // Forza ogni elemento a stringa
        return dealers.filter((d: any) => d != null).map((d: any) => typeof d === 'string' ? d : String(d));
      }
      return [];
    } catch (e) {
      console.warn('userFunctions non valido:', e);
      return [];
    }
  }

  /**
   * Costruisce una mappa dealerCode -> configurations unendo:
   * 1) configurazioni restituite insieme al dealer (se presenti)
   * 2) configurazioni presenti in userFunctions (fallback/merge)
   */
  private buildDealerConfigurationsMap(
    dealers: Dealer[],
    userFunctionConfigurations: Record<string, Configuration[]>
  ): Record<string, Configuration[]> {
    const map: Record<string, Configuration[]> = {};

    for (const dealer of dealers) {
      const dealerCode = dealer.dealerCode;
      if (!dealerCode) {
        continue;
      }

      const normalized = this.normalizeConfiguration(dealer.oprConfiguration);
      const fromDealer = normalized ? [normalized] : [];

      const fromUserFunctions = userFunctionConfigurations[dealerCode] || [];
      map[dealerCode] = this.mergeConfigurations(fromDealer, fromUserFunctions);
    }

    return map;
  }

  private normalizeConfiguration(value: unknown): Configuration | null {
    if (!value || typeof value !== 'object') {
      return null;
    }

    const candidate = value as Partial<Configuration>;
    if (!candidate.configurationCode || !candidate.configurationName) {
      return null;
    }

    return {
      ...candidate,
      configurationCode: String(candidate.configurationCode),
      configurationName: String(candidate.configurationName)
    };
  }

  private mergeConfigurations(primary: Configuration[], secondary: Configuration[]): Configuration[] {
    const byCode = new Map<string, Configuration>();

    for (const config of secondary) {
      if (config.configurationCode) {
        byCode.set(config.configurationCode, config);
      }
    }

    for (const config of primary) {
      if (config.configurationCode) {
        byCode.set(config.configurationCode, config);
      }
    }

    return Array.from(byCode.values());
  }

  getDealerConfigurations(dealerCode: string): Configuration[] {
    return this.dealerConfigurationsByCode[dealerCode] || [];
  }

  /**
   * Converte un Dealer (Opr_Dealers) in una Company da mostrare in UI.
   */
  private mapDealerToCompany(d: Dealer): Company {
    const street = [d.dealerOperatingAddressStreet, d.dealerOperatingAddressNumber]
      .filter(x => !!x).join(' ');
    return {
      companyId: d.dealerId,
      code: d.dealerCode,
      name: d.dealerName || d.dealerLegalName || d.dealerCode,
      vatNumber: d.dealerVatNumber || '',
      city: d.dealerOperatingAddressCity || d.dealerLegalAddressCity || '',
      address: street || d.dealerLegalAddressStreet || ''
    };
  }

  startTour(): void {
    this.tourGuideService.startTour(RETURN_REQUEST_TOUR_CONFIG);
  }

  /**
   * True se l'utente corrente ha ruolo ADMIN.
   */
  isAdmin(): boolean {
    const type = (this.user?.userType || '').toUpperCase();
    return type === 'ADMIN';
  }

  openReturnSettingsPopup(): void {
    this.isReturnSettingsPopupVisible = true;
  }

  closeReturnSettingsPopup(): void {
    this.isReturnSettingsPopupVisible = false;
  }

  openPrintPopup(request: ReturnRequest): void {
    this.printPopupRequest = request;
    this.printPopupDealer = this.dealersByCode[request.palletDealerCode] || null;
    // Nel popup vengono elencati tutti i resi della stessa bolla, non solo
    // la riga cliccata: la stampa label li copre tutti.
    const waybillCode = (request.palletWaybillCode || '').trim();
    this.printPopupPallets = waybillCode
      ? this.returnRequests.filter((r) => (r.palletWaybillCode || '').trim() === waybillCode)
      : [request];
    this.isPrintPopupVisible = true;
  }

  closePrintPopup(): void {
    this.isPrintPopupVisible = false;
    this.printPopupRequest = null;
    this.printPopupDealer = null;
    this.printPopupPallets = [];
  }

  /**
   * Apre il riepilogo con i resi appena creati (risposta del backend):
   * elenco pallet, elenco bolle e stampa multipla delle label.
   */
  private openSummaryPopup(pallets: Pallets[], dealerCode?: string): void {
    if (!pallets?.length) {
      return;
    }
    const code = dealerCode || pallets[0]?.palletDealerCode || this.selectedCompanyCode;
    this.summaryPopupPallets = pallets;
    this.summaryPopupDealer = (code && this.dealersByCode[code]) || null;
    this.isSummaryPopupVisible = true;
  }

  closeSummaryPopup(): void {
    this.isSummaryPopupVisible = false;
    this.summaryPopupPallets = [];
    this.summaryPopupDealer = null;
  }

  // ===== SECTION 1 helpers =====
  /**
   * True quando l'utente rappresenta una sola azienda: in quel caso la
   * selezione è obbligata e non può essere annullata (né dalla card della
   * sezione 1 né dal select 'Azienda' della sezione 2).
   */
  isSingleCompany(): boolean {
    return this.companies.length === 1;
  }

  selectCompany(code: string): void {
    // Con una sola azienda il click sulla card selezionata non deseleziona.
    if (this.isSingleCompany() && this.selectedCompanyCode === code) {
      return;
    }
    this.selectedCompanyCode = this.selectedCompanyCode === code ? '' : code;
    if (this.selectedCompanyCode) {
      this.selectedDealerConfigurations = this.getDealerConfigurations(this.selectedCompanyCode);
      this.deliveryTypes = this.selectedDealerConfigurations.map(c => ({
        code: c.configurationCode,
        name: c.configurationName
      }));
      this.loadReturnListForDealer(this.selectedCompanyCode);
      // Applica lo schema form ricavato dalla configurazione del dealer (oprConfiguration.configurationValue JSON).
      const dealerSchema = this.extractDealerFormSchema(this.selectedCompanyCode);
      this.selectedDealerHasConfig = !!dealerSchema;
      this.applyFormConfig(dealerSchema || this.getStaticFormConfig());
      this.returnForm.patchValue({ companyCode: this.selectedCompanyCode });
      this.applyAutoManagedFieldValues();
      return;
    }
    this.selectedDealerConfigurations = [];
    this.selectedDealerHasConfig = false;
    this.deliveryTypes = [];
    this.sysReturnList = [];
    this.returnNameOptions = [];
    this.applyFormConfig(this.getStaticFormConfig());
    this.applyAutoManagedFieldValues();
  }

  /**
   * True per il select 'Azienda' quando esiste una sola azienda: l'opzione
   * vuota va nascosta perché la selezione non è annullabile.
   */
  isCompanySelectLocked(field: ReturnRequestFormFieldConfig): boolean {
    return field.optionsSource === 'companies' && this.isSingleCompany();
  }

  /**
   * Nome azienda accorciato per la UI: oltre i 20 caratteri viene troncato
   * e chiuso con '...'. Il nome completo resta nel title dell'elemento.
   */
  truncateCompanyName(name: string | undefined | null, max: number = 20): string {
    const value = (name || '').trim();
    return value.length > max ? `${value.substring(0, max)}...` : value;
  }

  /** True se la card dell'azienda mostra il dettaglio esteso. */
  isCompanyExpanded(code: string): boolean {
    return this.expandedCompanyCodes.includes(code);
  }

  /**
   * Apre/chiude il dettaglio della card azienda.
   * stopPropagation: il click sulla card seleziona l'azienda, il bottone no.
   */
  toggleCompanyDetails(code: string, event: Event): void {
    event.stopPropagation();
    this.expandedCompanyCodes = this.isCompanyExpanded(code)
      ? this.expandedCompanyCodes.filter((c) => c !== code)
      : [...this.expandedCompanyCodes, code];
  }

  /**
   * Carica i record SYS_RETURN_LIST per il dealer selezionato.
   * Invocato a ogni cambio dealer, sia dalla card sia dal select del form
   * (entrambi passano da selectCompany).
   * - 'Tipologia reso' mostra il distinct di returnName;
   * - 'Tipologia Contenitore' mostra i returnPalletName filtrati per il
   *   returnName scelto (cascading, vedi getFieldOptions).
   */
  private loadReturnListForDealer(dealerCode: string): void {
    const clientBrands = this.extractClientBrands() as ClientBrandsEntry[] | undefined;
    this.returnRequestService.getReturnListByDealerCode(dealerCode, clientBrands).subscribe({
      next: (list: SysReturnList[]) => {
        this.sysReturnList = list || [];
        const distinctNames = Array.from(new Set(
          this.sysReturnList
            .map((r) => r.returnName)
            .filter((n): n is string => !!n)
        ));
        this.returnNameOptions = distinctNames.map((n) => ({ code: n, name: n }));
      },
      error: (err) => {
        console.error('Errore caricamento tipologie reso:', err);
        this.sysReturnList = [];
        this.returnNameOptions = [];
      }
    });
  }

  /**
   * Cambio azienda dal select 'Azienda' del form: sincronizza la selezione
   * globale (card in sezione 1) e rigenera schema + valori anagrafici
   * auto-gestiti tramite selectCompany. La selezione vuota deseleziona.
   */
  private onFormCompanyCodeChange(code: string): void {
    const next = (code || '').trim();
    if (next === this.selectedCompanyCode) {
      return;
    }
    // Con una sola azienda la selezione è obbligata: ripristina il valore nel
    // select invece di deselezionare (l'opzione vuota non viene comunque mostrata).
    if (!next && this.isSingleCompany()) {
      this.returnForm.get('companyCode')?.setValue(this.selectedCompanyCode, { emitEvent: false });
      return;
    }
    // selectCompany fa toggle: passargli il codice corrente equivale a deselezionare.
    this.selectCompany(next || this.selectedCompanyCode);
  }

  /**
   * Estrae lo schema form dal dealer selezionato leggendo
   * `dealer.oprConfiguration.configurationValue` (stringa JSON) e validandolo.
   */
  private extractDealerFormSchema(dealerCode: string): ReturnRequestFormConfig | null {
    const dealer = this.dealersByCode[dealerCode];
    const raw = dealer?.oprConfiguration?.configurationValue;
    if (!raw || typeof raw !== 'string') {
      return null;
    }
    try {
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object' || !Array.isArray(parsed.fields)) {
        return null;
      }
      const form: 'insert' | 'generate' = parsed.form === 'generate' ? 'generate' : 'insert';
      const fallback = this.getStaticFormConfig();
      return {
        version: parsed.version || '1.0.0',
        title: parsed.title || fallback.title,
        form,
        fields: parsed.fields,
        submission: parsed.submission || fallback.submission
      };
    } catch (e) {
      console.warn('oprConfiguration.configurationValue non è JSON valido:', e);
      return null;
    }
  }

  // ===== SECTION 2 helpers =====
  toggleFormCollapse(): void {
    this.isFormCollapsed = !this.isFormCollapsed;
  }

  toggleUserSectionCollapse(): void {
    this.isUserSectionCollapsed = !this.isUserSectionCollapsed;
  }

  isFieldInvalid(fieldName: string): boolean {
    const f = this.returnForm.get(fieldName);
    if (f?.disabled) {
      return false;
    }
    return !!(f && f.invalid && (f.touched || f.dirty));
  }

  isSystemManagedField(field: ReturnRequestFormFieldConfig): boolean {
    return field.managedBy === 'system' || field.managedBy === 'backend';
  }

  getFieldOptions(field: ReturnRequestFormFieldConfig, itemIndex?: number): { code: string; name: string }[] {
    switch (field.optionsSource) {
      case 'companies':
        return this.companies.map((company) => ({ code: company.code, name: company.name }));
      case 'deliveryTypes':
        return this.deliveryTypes;
      case 'returnNames':
        return this.returnNameOptions;
      case 'returnList': {
        // Tipologia Contenitore: returnPalletName filtrati per il returnName
        // selezionato (nel form generale o nella card pallet itemIndex).
        const selectedReturnName = itemIndex === undefined
          ? this.returnForm.get('returnName')?.value
          : this.palletsArray.at(itemIndex)?.get('returnName')?.value;
        const source = selectedReturnName
          ? this.sysReturnList.filter((r) => r.returnName === selectedReturnName)
          : this.sysReturnList;
        const options: { code: string; name: string }[] = [];
        source.forEach((r) => {
          if (!r.returnPalletName) {
            return;
          }
          const code = r.returnPalletType || r.returnPalletName;
          if (!options.some((o) => o.code === code)) {
            options.push({ code, name: r.returnPalletName });
          }
        });
        return options;
      }
      case 'palletStatuses':
        return this.palletStatuses;
      case 'warehouses':
        return this.warehouses;
      case 'carriers':
        return this.carriers;
      default:
        return [];
    }
  }

  trackByFormField(_index: number, field: ReturnRequestFormFieldConfig): string {
    return field.name;
  }

  /**
   * Testo dell'errore di validazione per il campo: distingue sforamento
   * dei limiti min/max dal semplice campo mancante. Per i campi delle card
   * pallet passare l'indice dell'elemento nel FormArray.
   */
  getFieldErrorText(field: ReturnRequestFormFieldConfig, index?: number): string {
    const control = index === undefined
      ? this.returnForm.get(field.name)
      : this.palletsArray.at(index)?.get(field.name);
    if (control?.hasError('max') && field.max !== undefined) {
      return `Valore massimo: ${field.max}`;
    }
    if (control?.hasError('min') && field.min !== undefined) {
      return `Valore minimo: ${field.min}`;
    }
    return 'Campo obbligatorio';
  }

  /**
   * Attributo step dell'input numerico ricavato dai decimali ammessi
   * (3 -> '0.001', 4 -> '0.0001'); null per i campi senza precisione dichiarata.
   */
  getFieldStep(field: ReturnRequestFormFieldConfig): string | null {
    if (field.type !== 'number' || field.decimals === undefined) {
      return null;
    }
    return field.decimals <= 0 ? '1' : `0.${'0'.repeat(field.decimals - 1)}1`;
  }

  /**
   * Tronca il valore ai decimali ammessi quando l'utente esce dal campo:
   * l'input type=number accetta qualsiasi precisione, il limite va imposto qui.
   * Per i campi delle card pallet passare l'indice dell'elemento nel FormArray.
   */
  roundFieldDecimals(field: ReturnRequestFormFieldConfig, index?: number): void {
    if (field.type !== 'number' || field.decimals === undefined) {
      return;
    }
    const control = index === undefined
      ? this.returnForm.get(field.name)
      : this.palletsArray.at(index)?.get(field.name);
    const value = Number(control?.value);
    if (!control || control.value === null || control.value === '' || !Number.isFinite(value)) {
      return;
    }
    const rounded = Number(value.toFixed(field.decimals));
    if (rounded !== value) {
      control.setValue(rounded);
    }
  }

  onSubmit(): void {
    if (this.returnForm.invalid) {
      this.returnForm.markAllAsTouched();
      return;
    }

    const v = this.returnForm.getRawValue();
    const company = this.companies.find(c => c.code === v.companyCode);

    if (this.formMode === 'generate') {
      this.submitGenerate(v, company);
      return;
    }

    // formMode === 'insert': il client costruisce N entità Pallets (una per ogni elemento
    // del FormArray 'pallets') e le invia in batch al backend.
    const palletsToCreate = this.buildPalletsFromForm(v);
    if (!palletsToCreate.length) {
      return;
    }

    const clientBrands = this.extractClientBrands() as ClientBrandsEntry[] | undefined;
    this.returnRequestService.createPalletsBatch(palletsToCreate, clientBrands).subscribe({
      next: (created: Pallets[]) => {
        const list = created && created.length ? created : palletsToCreate;
        this.notificationService.success(list.length === 1
          ? 'Richiesta di reso creata con successo'
          : `${list.length} richieste di reso create con successo`);
        this.afterPalletsCreated(list, v, company);
        this.openSummaryPopup(list, v.palletDealerCode || this.selectedCompanyCode);
      },
      error: (err) => {
        console.error('Errore creazione batch pallet:', err);
        this.notificationService.error('Errore nella creazione della richiesta di reso');
        // Fallback ottimistico: aggiungi comunque le entità lato UI.
        this.afterPalletsCreated(palletsToCreate, v, company);
      }
    });
  }

  /**
   * True se la quantità di pallet richiesta (modalità 'generate') è un intero
   * compreso tra 1 e maxPalletQuantity (99).
   */
  isPalletQuantityValid(): boolean {
    const quantity = Number(this.palletQuantity);
    return Number.isInteger(quantity)
      && quantity >= 1
      && quantity <= this.maxPalletQuantity;
  }

  /**
   * Incrementa/decrementa il numero di resi restando nel range 1..maxPalletQuantity.
   * Un valore non valido (campo svuotato, NaN) riparte da 1.
   */
  changePalletQuantity(delta: number): void {
    const current = Number(this.palletQuantity);
    const base = Number.isInteger(current) ? current : 1;
    const next = base + delta;
    this.palletQuantity = Math.min(this.maxPalletQuantity, Math.max(1, next));
  }

  /**
   * Modalità 'generate' (bottone "Genera reso"): invia a
   * POST /api/return-request/create il payload minimale concordato:
   * dealer, codice bolla, peso, volume e tipologia (reso / contenitore),
   * più clientBrands per il check permessi. Il pallet viene replicato
   * palletQuantity volte (max 99) nell'array `pallets` del body: il backend
   * crea N pallet con lo stesso codice bolla. palletCode (sequence),
   * palletDate (oggi) e palletStatus (REQUEST_RETURN) li imposta il server.
   */
  private submitGenerate(v: any, company: Company | undefined): void {
    if (!this.isPalletQuantityValid()) {
      this.notificationService.warning(`Il numero di resi deve essere compreso tra 1 e ${this.maxPalletQuantity}`);
      return;
    }
    // buildPalletsFromForm applica il mapFormToPallet dello schema dealer:
    // così i nomi custom dei campi form arrivano già sulle proprietà pallet.
    const mapped = this.buildPalletsFromForm(v)[0] || {};

    // 'Tipologia reso' e 'Tipologia Contenitore' viaggiano concatenate in
    // palletDeliveryType: per il contenitore si usa il returnPalletName
    // (etichetta), non il value del select (returnPalletType).
    const containerName = this.resolveContainerName(v.returnName, mapped.palletType ?? v.palletType);
    const deliveryType = [v.returnName, containerName]
      .filter((x: any) => !!x)
      .join(' / ');

    const pallet: Pallets = {
      palletDealerCode: mapped.palletDealerCode || this.selectedCompanyCode,
      // Località di origine del reso: il dealer che lo richiede.
      palletOriginLocation: mapped.palletOriginLocation || mapped.palletDealerCode || this.selectedCompanyCode,
      // Il codice bolla digitato nel form è quello del cliente: viaggia in
      // palletWaybillCodeExternal; palletWaybillCode lo genera il server (sequence).
      palletWaybillCodeExternal: mapped.palletWaybillCode ?? v.palletWaybillCode,
      palletDeliveryType: deliveryType,
      palletNote: mapped.palletNote ?? v.notes
    };

    // Replica il pallet per la quantità richiesta: il backend genera per
    // ognuno il proprio palletCode e condivide lo stesso codice bolla.
    const quantity = Number(this.palletQuantity);
    const palletsToCreate: Pallets[] = Array.from({ length: quantity }, () => ({ ...pallet }));

    // Peso e volume del form sono i totali della richiesta: vanno ripartiti
    // sui pallet creati (il resto degli arrotondamenti finisce sul primo, così
    // la somma dei pallet resta uguale al totale digitato).
    this.splitTotalOverPallets(
      palletsToCreate, 'palletWeight', mapped.palletWeight ?? v.palletWeight, 3
    );
    this.splitTotalOverPallets(
      palletsToCreate, 'palletVolume', mapped.palletVolume ?? v.palletVolume, 4
    );

    const clientBrands = this.extractClientBrands() as ClientBrandsEntry[] | undefined;
    this.returnRequestService.createPallet(palletsToCreate, clientBrands).subscribe({
      next: (created: Pallets[]) => {
        const list = created && created.length ? created : palletsToCreate;
        const code = list[0]?.palletCode;
        this.notificationService.success(list.length === 1
          ? (code ? `Reso ${code} creato con successo` : 'Reso creato con successo')
          : `${list.length} resi creati con successo`);
        this.afterPalletsCreated(list, v, company);
        this.openSummaryPopup(list, v.palletDealerCode || this.selectedCompanyCode);
      },
      error: (err) => {
        console.error('Errore creazione pallet:', err);
        this.notificationService.error('Errore nella creazione del reso');
      }
    });
  }

  /**
   * Ripartisce un valore totale (peso o volume) sui pallet da creare.
   * Ogni pallet riceve total/N arrotondato ai decimali ammessi; l'eventuale
   * scarto di arrotondamento viene assorbito dal primo pallet, così la somma
   * dei valori coincide con il totale digitato nel form.
   */
  private splitTotalOverPallets(
    pallets: Pallets[],
    property: 'palletWeight' | 'palletVolume',
    total: any,
    decimals: number
  ): void {
    const count = pallets.length;
    if (!count) {
      return;
    }

    const totalValue = Number(total);
    if (total === null || total === undefined || total === '' || !Number.isFinite(totalValue)) {
      pallets.forEach((pallet) => delete pallet[property]);
      return;
    }

    const share = Number((totalValue / count).toFixed(decimals));
    pallets.forEach((pallet) => pallet[property] = share);
    // Primo pallet: assorbe la differenza dovuta all'arrotondamento.
    pallets[0][property] = Number((totalValue - share * (count - 1)).toFixed(decimals));
  }

  /**
   * Risolve il returnPalletName del contenitore selezionato: il select
   * 'Tipologia Contenitore' usa come value returnPalletType (vedi
   * getFieldOptions case 'returnList'), ma verso il backend va il nome.
   * Se non c'è match in SYS_RETURN_LIST torna il valore così com'è.
   */
  private resolveContainerName(returnName: string | undefined, palletTypeValue: string | undefined): string | undefined {
    if (!palletTypeValue) {
      return undefined;
    }
    const match = this.sysReturnList.find((r) =>
      (!returnName || r.returnName === returnName)
      && (r.returnPalletType || r.returnPalletName) === palletTypeValue
    );
    return match?.returnPalletName || palletTypeValue;
  }

  /**
   * Aggiorna lo stato UI dopo la creazione (lato client o lato server) delle entità Pallets.
   */
  private afterPalletsCreated(pallets: Pallets[], v: any, company: Company | undefined): void {
    this.createdPallets.unshift(...pallets);
    const createdAt = new Date().toISOString();
    const newRequests: ReturnRequest[] = pallets.map((pallet, index) => ({
      id: Date.now() + index,
      companyCode: v.companyCode,
      companyName: company?.name || '',
      palletCode: pallet.palletCode || '',
      palletWaybillCode: pallet.palletWaybillCode || '',
      palletShipmentCode: pallet.palletShipmentCode || '',
      palletDate: pallet.palletDate || '',
      palletDeliveryType: pallet.palletDeliveryType || '',
      palletDealerCode: pallet.palletDealerCode || '',
      dealerName: v.dealerName || '',
      dealerOperatingAddressStreet: v.dealerOperatingAddressStreet || '',
      dealerOperatingAddressCity: v.dealerOperatingAddressCity || '',
      palletStatus: pallet.palletStatus || 'REQUESTED',
      palletEstimatedDeliveryDate: pallet.palletEstimatedDeliveryDate,
      warehouseCode: v.warehouseCode || '',
      carrierCode: v.carrierCode || '',
      notes: pallet.palletNote || '',
      createdAt,
      ...pallet
    }));

    this.returnRequests.unshift(...newRequests);
    // Il conteggio arriva dal backend (paginazione server-side): senza questo
    // allineamento la paginazione resta nascosta (*ngIf="totalCount > 0")
    // quando la tabella era vuota prima della creazione.
    this.totalCount += newRequests.length;
    this.applyFilters();
    this.resetForm();
  }

  /**
   * Costruisce le entità Pallets da inviare al backend (modalità 'insert').
   * Per ogni elemento del FormArray 'pallets' fonde i valori del singolo pallet
   * con i valori generali (companyCode, dealerName, ecc.).
   * Se sono presenti configurazioni dealer multiple, ogni pallet viene moltiplicato
   * per il numero di configurazioni (palletType).
   */
  private buildPalletsFromForm(v: any): Pallets[] {
    const mapFormToPallet = this.formConfig?.submission.mapFormToPallet || {
      companyCode: 'palletClientCode',
      palletCode: 'palletCode',
      palletWaybillCode: 'palletWaybillCode',
      palletShipmentCode: 'palletShipmentCode',
      palletDate: 'palletDate',
      palletEstimatedDeliveryDate: 'palletEstimatedDeliveryDate',
      palletDeliveryType: 'palletDeliveryType',
      palletDealerCode: 'palletDealerCode',
      palletStatus: 'palletStatus',
      palletType: 'palletType',
      palletWeight: 'palletWeight',
      palletVolume: 'palletVolume',
      carrierCode: 'palletCarrierCode',
      notes: 'palletNote'
    } as Record<string, keyof Pallets>;

    const createdAt = new Date().toISOString();
    const generalRaw: Record<string, any> = {};
    this.generalFields.forEach((f) => generalRaw[f.name] = v[f.name]);

    const palletItems: any[] = Array.isArray(v.pallets) && v.pallets.length
      ? v.pallets
      : [{}];

    const configurationTypes = this.selectedDealerConfigurations
      .map((config) => config.configurationCode)
      .filter((code): code is string => !!code);

    const useMultiByConfig = this.formConfig?.submission.multiCreateBy === 'dealerConfigurations'
      && configurationTypes.length > 0;

    const result: Pallets[] = [];
    palletItems.forEach((item) => {
      const merged: Record<string, any> = { ...generalRaw, ...item };
      const pallet: Pallets = { palletCreatedAt: createdAt };
      Object.entries(mapFormToPallet).forEach(([formControlName, palletProperty]) => {
        const value = merged[formControlName];
        if (value !== undefined && value !== null && value !== '') {
          pallet[palletProperty] = value;
        }
      });
      if (!pallet.palletStatus) {
        pallet.palletStatus = 'REQUESTED';
      }
      // La località di origine del reso è il dealer stesso: stesso codice.
      if (!pallet.palletOriginLocation) {
        pallet.palletOriginLocation = pallet.palletDealerCode || this.selectedCompanyCode;
      }
      // Se l'utente ha scelto esplicitamente la Tipologia reso (palletType),
      // non si moltiplica più per tutte le configurazioni del dealer.
      if (useMultiByConfig && !pallet.palletType) {
        configurationTypes.forEach((configurationType) => {
          result.push({ ...pallet, palletType: configurationType });
        });
      } else {
        result.push(pallet);
      }
    });

    return result;
  }

  resetForm(): void {
    const defaultValues: Record<string, any> = {};
    this.generalFields.forEach((field) => {
      if (field.defaultValue !== undefined) {
        defaultValues[field.name] = field.defaultValue;
      }
    });

    defaultValues['companyCode'] = this.selectedCompanyCode || defaultValues['companyCode'] || '';

    if (this.formMode === 'insert') {
      // Resetta il FormArray: una sola riga vuota.
      this.clearPalletsArray();
      this.palletsArray.push(this.buildPalletItemGroup());
      this.collapsedPalletStates = { 0: false };
    } else {
      this.palletQuantity = 1;
    }

    this.returnForm.reset({ ...defaultValues, pallets: this.palletsArray.value });
    this.applyAutoManagedFieldValues();
  }

  // ===== Pallets FormArray helpers (modalità 'insert') =====
  get palletsArray(): FormArray {
    return this.returnForm.get('pallets') as FormArray;
  }

  /** Crea un FormGroup per un singolo pallet (modalità insert) con i soli campi 'item'. */
  private buildPalletItemGroup(initial?: Record<string, any>): FormGroup {
    const controls: Record<string, any[]> = {};
    this.itemFields.forEach((field) => {
      const validators = field.required ? [Validators.required] : [];
      if (field.min !== undefined) {
        validators.push(Validators.min(field.min));
      }
      if (field.max !== undefined) {
        validators.push(Validators.max(field.max));
      }
      const value = initial?.[field.name] ?? field.defaultValue ?? '';
      controls[field.name] = [value, validators];
    });
    const group = this.fb.group(controls);

    // Cascading per-card: al cambio di 'Tipologia reso' azzera il contenitore;
    // il select contenitore si attiva solo dopo la scelta della tipologia.
    const returnNameControl = group.get('returnName');
    if (returnNameControl) {
      returnNameControl.valueChanges.subscribe(() => {
        group.get('palletType')?.setValue('', { emitEvent: false });
        this.syncContainerControlState(group);
      });
    }
    this.syncContainerControlState(group);

    return group;
  }

  /**
   * 'Tipologia Contenitore' si attiva solo dopo aver scelto 'Tipologia reso':
   * abilita/disabilita il controllo palletType in base al valore di returnName.
   * Se uno dei due campi non esiste nello schema corrente non fa nulla.
   */
  private syncContainerControlState(group: FormGroup): void {
    const containerControl = group.get('palletType');
    const returnNameControl = group.get('returnName');
    if (!containerControl || !returnNameControl) {
      return;
    }
    if (returnNameControl.value) {
      containerControl.enable({ emitEvent: false });
    } else {
      containerControl.disable({ emitEvent: false });
    }
  }

  private clearPalletsArray(): void {
    while (this.palletsArray.length > 0) {
      this.palletsArray.removeAt(0);
    }
  }

  addPallet(): void {
    if (this.isMaxPalletsReached()) {
      return;
    }
    this.palletsArray.push(this.buildPalletItemGroup());
    this.collapsedPalletStates[this.palletsArray.length - 1] = false;
  }

  removePallet(index: number): void {
    if (this.palletsArray.length <= 1) {
      return;
    }
    this.palletsArray.removeAt(index);
    // Riallinea la mappa di collapse.
    const next: Record<number, boolean> = {};
    this.palletsArray.controls.forEach((_c, i) => {
      next[i] = this.collapsedPalletStates[i >= index ? i + 1 : i] || false;
    });
    this.collapsedPalletStates = next;
  }

  resetPallets(): void {
    this.clearPalletsArray();
    this.palletsArray.push(this.buildPalletItemGroup());
    this.collapsedPalletStates = { 0: false };
  }

  isMaxPalletsReached(): boolean {
    return this.palletsArray.length >= this.maxPalletQuantity;
  }

  isApproachingMaxPallets(): boolean {
    return this.palletsArray.length >= this.maxPalletQuantity - 4
      && this.palletsArray.length < this.maxPalletQuantity;
  }

  toggleItemCollapsed(index: number): void {
    this.collapsedPalletStates[index] = !this.collapsedPalletStates[index];
  }

  isItemCollapsed(index: number): boolean {
    return !!this.collapsedPalletStates[index];
  }

  isItemFieldInvalid(index: number, fieldName: string): boolean {
    const control = this.palletsArray.at(index)?.get(fieldName);
    if (!control || control.disabled) {
      return false;
    }
    return !!(control.invalid && (control.touched || control.dirty));
  }

  trackByPalletIndex(index: number, _control: any): number {
    return index;
  }

  /**
   * Garantisce che Tipologia reso / Peso / Volume siano sempre presenti nel
   * form, anche quando lo schema del dealer (configurationValue da DB) non li
   * dichiara. I campi già definiti dallo schema non vengono duplicati.
   */
  /**
   * Inserisce 'Totale' nell'etichetta prima dell'unità di misura:
   * 'Peso (kg)' -> 'Peso Totale (kg)'.
   */
  private withTotalLabel(label: string | undefined): string {
    const value = (label || '').trim();
    if (!value) {
      return 'Totale';
    }
    const unitIndex = value.indexOf('(');
    if (unitIndex <= 0) {
      return `${value} Totale`;
    }
    return `${value.slice(0, unitIndex).trim()} Totale ${value.slice(unitIndex).trim()}`;
  }

  private ensureExtraReturnFields(config: ReturnRequestFormConfig): ReturnRequestFormConfig {
    const extraFields: ReturnRequestFormFieldConfig[] = [
      { name: 'returnName', label: 'Tipologia reso', type: 'select', required: true, optionsSource: 'returnNames', scope: 'general' },
      { name: 'palletType', label: 'Tipologia Contenitore', type: 'select', required: true, optionsSource: 'returnList', scope: 'general' },
      // Peso e volume sono i totali della richiesta: in 'generate' vengono
      // ripartiti sui pallet creati (vedi splitTotalOverPallets).
      { name: 'palletWeight', label: 'Peso Totale (kg)', type: 'number', required: true, min: 0, max: 19999, decimals: 3, scope: 'general', colClass: 'col-md-2 mb-3' },
      { name: 'palletVolume', label: 'Volume Totale (m³)', type: 'number', required: true, min: 0, max: 999, decimals: 4, scope: 'general', colClass: 'col-md-2 mb-3' }
    ];

    // I campi extra mancanti vengono anteposti: devono stare nella prima riga del form.
    const fields = [...(config.fields || [])];
    const missing = extraFields.filter((extra) => !fields.some((f) => f.name === extra.name));
    fields.unshift(...missing);

    // Peso e volume hanno una precisione fissa (3 e 4 decimali) anche quando
    // sono lo schema del dealer a dichiararli senza specificarla; codice reso
    // e codice bolla sono limitati a 99 caratteri.
    const decimalsByField: Record<string, number> = { palletWeight: 3, palletVolume: 4 };
    const maxLengthByField: Record<string, number> = { palletCode: 99, palletWaybillCode: 99 };
    // Peso e volume sono totali di richiesta anche quando lo schema del dealer
    // li etichetta come valori del singolo pallet: l'etichetta va allineata.
    const totalLabelFields = ['palletWeight', 'palletVolume'];
    fields.forEach((field, index) => {
      const decimals = decimalsByField[field.name];
      const maxLength = maxLengthByField[field.name];
      let resolved = field;
      if (decimals !== undefined && resolved.decimals === undefined) {
        resolved = { ...resolved, decimals };
      }
      if (maxLength !== undefined && resolved.maxLength === undefined) {
        resolved = { ...resolved, maxLength };
      }
      if (totalLabelFields.includes(resolved.name) && !/totale/i.test(resolved.label || '')) {
        resolved = { ...resolved, label: this.withTotalLabel(resolved.label) };
      }
      if (resolved !== field) {
        fields[index] = resolved;
      }
    });

    const fallbackSubmission = this.getStaticFormConfig().submission;
    const mapFormToPallet: Record<string, keyof Pallets> = {
      ...(config.submission?.mapFormToPallet || fallbackSubmission.mapFormToPallet)
    };
    if (!mapFormToPallet['palletType']) {
      mapFormToPallet['palletType'] = 'palletType';
    }
    if (!mapFormToPallet['palletWeight']) {
      mapFormToPallet['palletWeight'] = 'palletWeight';
    }
    if (!mapFormToPallet['palletVolume']) {
      mapFormToPallet['palletVolume'] = 'palletVolume';
    }

    return {
      ...config,
      fields,
      submission: {
        ...(config.submission || fallbackSubmission),
        mapFormToPallet
      }
    };
  }

  private applyFormConfig(config: ReturnRequestFormConfig): void {
    config = this.ensureExtraReturnFields(config);
    this.formConfig = config;
    this.formFields = config.fields || [];
    this.formMode = config.form === 'generate' ? 'generate' : 'insert';
    if (this.palletQuantity < 1 || this.palletQuantity > this.maxPalletQuantity) {
      this.palletQuantity = 1;
    }

    // Suddivide i campi in 'general' (a livello richiesta) e 'item' (per singolo pallet).
    // - In modalità 'generate' rispettiamo gli scope dichiarati (default 'general').
    // - In modalità 'insert' TUTTI i campi compilabili dall'utente diventano 'item'
    //   (così ogni pallet può avere i propri valori, analogo a "Dati dei Colli" di
    //   shipment-request). I campi system/backend-managed restano 'general' perché
    //   vengono valorizzati automaticamente una volta sola e poi fusi su ogni pallet.
    const resolveScope = (f: ReturnRequestFormFieldConfig): 'general' | 'item' => {
      if (this.formMode === 'insert') {
        if (this.isSystemManagedField(f)) {
          return 'general';
        }
        return 'item';
      }
      return f.scope || 'general';
    };

    this.generalFields = this.formFields.filter((f) => resolveScope(f) === 'general');
    this.itemFields = this.formFields.filter((f) => resolveScope(f) === 'item');

    const controlsConfig: Record<string, any[] | FormArray> = {};
    this.generalFields.forEach((field) => {
      // In modalità 'insert' la sezione dei campi generali NON viene mostrata (l'utente
      // compila solo le card dei singoli pallet), quindi non possiamo bloccare il submit
      // su required di campi non visibili. I valori vengono comunque garantiti tramite
      // autoValueFrom / defaultValue / selectedCompanyCode.
      const validators = (field.required && this.formMode === 'generate') ? [Validators.required] : [];
      if (field.min !== undefined) {
        validators.push(Validators.min(field.min));
      }
      if (field.max !== undefined) {
        validators.push(Validators.max(field.max));
      }
      const resolvedAutoValue = this.resolveAutoValue(field);
      const initialValue = resolvedAutoValue ?? (field.name === 'companyCode'
        ? (this.selectedCompanyCode || field.defaultValue || '')
        : (field.defaultValue || ''));
      controlsConfig[field.name] = [initialValue, validators];
    });

    // FormArray dei pallet: usato solo in modalità 'insert' ma sempre presente nel form.
    controlsConfig['pallets'] = this.fb.array([]);

    this.returnForm = this.fb.group(controlsConfig as any);

    // Il select 'Azienda' del form deve pilotare la selezione globale:
    // sincronizza la card evidenziata in sezione 1 e i dati anagrafici auto-gestiti.
    this.companyCodeChangesSub?.unsubscribe();
    const companyCodeControl = this.returnForm.get('companyCode');
    if (companyCodeControl) {
      this.companyCodeChangesSub = companyCodeControl.valueChanges
        .subscribe((code: string) => this.onFormCompanyCodeChange(code));
    }

    // Cascading: al cambio di 'Tipologia reso' il contenitore selezionato
    // potrebbe non essere più valido -> viene azzerato; il select contenitore
    // resta disabilitato finché non viene scelta una tipologia.
    this.returnNameChangesSub?.unsubscribe();
    const returnNameControl = this.returnForm.get('returnName');
    if (returnNameControl) {
      this.returnNameChangesSub = returnNameControl.valueChanges.subscribe(() => {
        this.returnForm.get('palletType')?.setValue('', { emitEvent: false });
        this.syncContainerControlState(this.returnForm);
      });
    }

    if (this.formMode === 'insert' && this.itemFields.length) {
      this.palletsArray.push(this.buildPalletItemGroup());
      this.collapsedPalletStates = { 0: false };
    } else {
      this.collapsedPalletStates = {};
    }

    this.applyAutoManagedFieldValues();
  }

  private applyAutoManagedFieldValues(): void {
    if (!this.returnForm || !this.generalFields.length) {
      return;
    }

    this.generalFields.forEach((field) => {
      const control = this.returnForm.get(field.name);
      if (!control) {
        return;
      }

      const autoValue = this.resolveAutoValue(field);
      if (autoValue !== undefined && autoValue !== null) {
        control.patchValue(autoValue, { emitEvent: false });
      }

      if (this.isSystemManagedField(field)) {
        control.disable({ emitEvent: false });
      } else {
        control.enable({ emitEvent: false });
      }
    });

    // Il giro di enable() sopra riabilita anche il contenitore: va
    // risincronizzato con lo stato di 'Tipologia reso'.
    this.syncContainerControlState(this.returnForm);

    this.returnForm.updateValueAndValidity({ emitEvent: false });
  }

  private resolveAutoValue(field: ReturnRequestFormFieldConfig): string | undefined {
    if (!field.autoValueFrom) {
      return undefined;
    }

    const selectedCode = this.selectedCompanyCode || this.returnForm.get('companyCode')?.value || '';
    const selectedCompany = this.companies.find((company) => company.code === selectedCode);

    switch (field.autoValueFrom) {
      case 'selectedCompanyCode':
        return selectedCode || undefined;
      case 'selectedCompanyName':
        return selectedCompany?.name || undefined;
      case 'selectedCompanyCity':
        return selectedCompany?.city || undefined;
      case 'selectedCompanyAddress':
        return selectedCompany?.address || undefined;
      case 'todayDate':
        return new Date().toISOString().slice(0, 10);
      default:
        return undefined;
    }
  }

  private getStaticFormConfig(): ReturnRequestFormConfig {
    return {
      version: 'fallback',
      title: 'Nuova richiesta di reso',
      fields: [
        { name: 'companyCode', label: 'Azienda', type: 'select', required: true, managedBy: 'user', optionsSource: 'companies', scope: 'general' },
        // Codici di abbinamento: per-pallet (insert), nascosti in 'generate'.
        { name: 'palletCode', label: 'Pallet Code', type: 'text', required: true, scope: 'item' },
        { name: 'palletWaybillCode', label: 'Waybill Code', type: 'text', scope: 'item' },
        { name: 'palletShipmentCode', label: 'Shipment Code', type: 'text', scope: 'item' },
        { name: 'palletDate', label: 'Pallet Date', type: 'date', required: true, managedBy: 'frontend', autoValueFrom: 'todayDate', scope: 'general' },
        { name: 'palletDeliveryType', label: 'Delivery Type', type: 'select', required: true, optionsSource: 'deliveryTypes', scope: 'general' },
        { name: 'palletDealerCode', label: 'Dealer Code', type: 'text', required: true, managedBy: 'system', autoValueFrom: 'selectedCompanyCode', scope: 'general' },
        { name: 'dealerName', label: 'Dealer Name', type: 'text', required: true, managedBy: 'system', autoValueFrom: 'selectedCompanyName', scope: 'general' },
        { name: 'dealerOperatingAddressStreet', label: 'Dealer Address (Street)', type: 'text', managedBy: 'system', autoValueFrom: 'selectedCompanyAddress', scope: 'general' },
        { name: 'dealerOperatingAddressCity', label: 'Dealer City', type: 'text', managedBy: 'system', autoValueFrom: 'selectedCompanyCity', scope: 'general' },
        { name: 'palletStatus', label: 'Pallet Status', type: 'select', required: true, optionsSource: 'palletStatuses', defaultValue: 'REQUESTED', scope: 'general' },
        { name: 'palletEstimatedDeliveryDate', label: 'Estimated Delivery Date', type: 'date', scope: 'general' },
        { name: 'warehouseCode', label: 'Warehouse', type: 'select', optionsSource: 'warehouses', scope: 'general' },
        { name: 'carrierCode', label: 'Carrier', type: 'select', optionsSource: 'carriers', scope: 'general' },
        { name: 'notes', label: 'Note', type: 'textarea', scope: 'general' }
      ],
      submission: {
        multiCreateBy: 'dealerConfigurations',
        mapFormToPallet: {
          companyCode: 'palletClientCode',
          palletCode: 'palletCode',
          palletWaybillCode: 'palletWaybillCode',
          palletShipmentCode: 'palletShipmentCode',
          palletDate: 'palletDate',
          palletEstimatedDeliveryDate: 'palletEstimatedDeliveryDate',
          palletDeliveryType: 'palletDeliveryType',
          palletDealerCode: 'palletDealerCode',
          palletStatus: 'palletStatus',
          palletType: 'palletType',
          palletWeight: 'palletWeight',
          palletVolume: 'palletVolume',
          carrierCode: 'palletCarrierCode',
          notes: 'palletNote'
        }
      }
    };
  }

  // ===== SECTION 3 helpers =====
  /**
   * Filtro client-side sulle righe già scaricate. Codice reso, codice bolla e
   * intervallo date NON sono qui: li applica il backend in
   * getPalletsByDealerCodes, rifiltrarli lato client nasconderebbe righe
   * legittime quando le due logiche non coincidono.
   */
  applyFilters(): void {
    const term = this.globalSearchTerm.trim().toLowerCase();
    const shipmentCode = this.filterShipmentCode.trim().toLowerCase();
    const dealerCode = this.filterDealerCode.trim().toLowerCase();
    const dealerName = this.filterDealerName.trim().toLowerCase();
    const dealerCity = this.filterDealerCity.trim().toLowerCase();

    this.filteredRequests = this.returnRequests.filter(r => {
      const matchesTerm = !term || Object.values(r).some(v =>
        v != null && v.toString().toLowerCase().includes(term)
      );
      const matchesShipment = !shipmentCode || (r.palletShipmentCode || '').toLowerCase().includes(shipmentCode);
      const matchesDealerCode = !dealerCode || r.palletDealerCode.toLowerCase().includes(dealerCode);
      const matchesDealerName = !dealerName || r.dealerName.toLowerCase().includes(dealerName);
      const matchesDealerCity = !dealerCity || (r.dealerOperatingAddressCity || '').toLowerCase().includes(dealerCity);
      const matchesStatus = !this.filterStatus || r.palletStatus === this.filterStatus;
      const matchesDeliveryType = !this.filterDeliveryType || r.palletDeliveryType === this.filterDeliveryType;
      const matchesWarehouse = !this.filterWarehouse || r.warehouseCode === this.filterWarehouse;
      const matchesCarrier = !this.filterCarrier || r.carrierCode === this.filterCarrier;

      return matchesTerm && matchesShipment
        && matchesDealerCode && matchesDealerName && matchesDealerCity
        && matchesStatus && matchesDeliveryType && matchesWarehouse && matchesCarrier;
    });
  }

  // ===== Filtri data (stesso comportamento di shipment-management) =====
  /** Data Da formattata per l'input readonly (dd/MM/yyyy HH:mm). */
  getFormattedFilterDateFrom(): string {
    return this.formatFilterDate(this.selectedFilterDateFrom);
  }

  /** Data A formattata per l'input readonly (dd/MM/yyyy HH:mm). */
  getFormattedFilterDateTo(): string {
    return this.formatFilterDate(this.selectedFilterDateTo);
  }

  private formatFilterDate(date: Date | null): string {
    if (!date) {
      return '';
    }
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${day}/${month}/${year} ${hours}:${minutes}`;
  }

  /**
   * Flatpickr del filtro "Data Da": alla scelta di un giorno diverso l'ora
   * viene riportata a 00:00, altrimenti resta quella impostata dall'utente.
   */
  private initializeFilterDateFromFlatpickr(): void {
    if (!this.filterDateFromInput?.nativeElement) {
      return;
    }
    const currentLang = this.translate.currentLang || 'it';
    this.filterDateFromFlatpickrInstance?.destroy();

    this.filterDateFromFlatpickrInstance = flatpickr(this.filterDateFromInput.nativeElement, {
      dateFormat: 'd/m/Y H:i',
      enableTime: true,
      time_24hr: true,
      allowInput: false,
      clickOpens: false,
      locale: currentLang === 'it' ? Italian : undefined,
      defaultDate: this.selectedFilterDateFrom || undefined,
      defaultHour: 0,
      defaultMinute: 0,
      onChange: (selectedDates: Date[]) => {
        if (!selectedDates.length) {
          return;
        }
        const selectedDate = new Date(selectedDates[0]);
        const isDifferentDay = this.selectedFilterDateFrom
          && this.selectedFilterDateFrom.toDateString() !== selectedDate.toDateString();
        if (!this.selectedFilterDateFrom || isDifferentDay) {
          selectedDate.setHours(0, 0, 0, 0);
        }
        this.selectedFilterDateFrom = selectedDate;
        this.filterDateFromFlatpickrInstance?.close();
        this.syncFilterDateToMinDate();
      },
      onClose: () => {
        this.filterDateFromInput?.nativeElement?.blur();
      }
    });
  }

  /**
   * Flatpickr del filtro "Data A": stesso schema di "Data Da" ma con ora
   * di default a fine giornata (23:59), così l'intervallo è inclusivo.
   */
  private initializeFilterDateToFlatpickr(): void {
    if (!this.filterDateToInput?.nativeElement) {
      return;
    }
    const currentLang = this.translate.currentLang || 'it';
    this.filterDateToFlatpickrInstance?.destroy();

    this.filterDateToFlatpickrInstance = flatpickr(this.filterDateToInput.nativeElement, {
      dateFormat: 'd/m/Y H:i',
      enableTime: true,
      time_24hr: true,
      allowInput: false,
      clickOpens: false,
      locale: currentLang === 'it' ? Italian : undefined,
      defaultDate: this.selectedFilterDateTo || undefined,
      defaultHour: 23,
      defaultMinute: 59,
      // Con "Data Da" impostata il calendario non permette giorni precedenti.
      minDate: this.selectedFilterDateFrom || undefined,
      onChange: (selectedDates: Date[]) => {
        if (!selectedDates.length) {
          return;
        }
        const selectedDate = new Date(selectedDates[0]);
        const isDifferentDay = this.selectedFilterDateTo
          && this.selectedFilterDateTo.toDateString() !== selectedDate.toDateString();
        if (!this.selectedFilterDateTo || isDifferentDay) {
          selectedDate.setHours(23, 59, 59, 999);
        }
        this.selectedFilterDateTo = this.clampFilterDateTo(selectedDate);
        this.filterDateToFlatpickrInstance?.setDate(this.selectedFilterDateTo, false);
        this.filterDateToFlatpickrInstance?.close();
      },
      onClose: () => {
        this.filterDateToInput?.nativeElement?.blur();
      }
    });
  }

  openFilterDateFromPicker(): void {
    if (!this.filterDateFromFlatpickrInstance) {
      this.initializeFilterDateFromFlatpickr();
    }
    if (this.filterDateFromFlatpickrInstance && !this.filterDateFromFlatpickrInstance.isOpen) {
      if (this.selectedFilterDateFrom) {
        this.filterDateFromFlatpickrInstance.setDate(this.selectedFilterDateFrom);
      }
      this.filterDateFromFlatpickrInstance.open();
    }
  }

  openFilterDateToPicker(): void {
    if (!this.filterDateToFlatpickrInstance) {
      this.initializeFilterDateToFlatpickr();
    }
    if (this.filterDateToFlatpickrInstance && !this.filterDateToFlatpickrInstance.isOpen) {
      if (this.selectedFilterDateTo) {
        this.filterDateToFlatpickrInstance.setDate(this.selectedFilterDateTo);
      }
      this.filterDateToFlatpickrInstance.open();
    }
  }

  decrementFilterDateFrom(): void {
    this.shiftFilterDateFrom(-1);
  }

  incrementFilterDateFrom(): void {
    this.shiftFilterDateFrom(1);
  }

  /** Sposta "Data Da" di N giorni mantenendo l'ora a inizio giornata. */
  private shiftFilterDateFrom(days: number): void {
    const base = this.selectedFilterDateFrom ? new Date(this.selectedFilterDateFrom) : new Date();
    base.setDate(base.getDate() + (this.selectedFilterDateFrom ? days : 0));
    base.setHours(0, 0, 0, 0);
    this.selectedFilterDateFrom = base;
    this.filterDateFromFlatpickrInstance?.setDate(this.selectedFilterDateFrom);
    this.syncFilterDateToMinDate();
  }

  setFilterDateFromToday(): void {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    this.selectedFilterDateFrom = today;
    this.filterDateFromFlatpickrInstance?.setDate(this.selectedFilterDateFrom);
    this.syncFilterDateToMinDate();
  }

  clearFilterDateFrom(): void {
    this.selectedFilterDateFrom = null;
    this.filterDateFromFlatpickrInstance?.clear();
    this.syncFilterDateToMinDate();
  }

  /**
   * Riporta un candidato "Data A" entro il minimo consentito: con "Data Da"
   * impostata l'intervallo non può essere invertito, quindi il valore viene
   * allineato alla fine della giornata di "Data Da".
   */
  private clampFilterDateTo(candidate: Date): Date {
    const from = this.selectedFilterDateFrom;
    if (!from || candidate.getTime() >= from.getTime()) {
      return candidate;
    }
    const clamped = new Date(from);
    clamped.setHours(23, 59, 59, 999);
    return clamped;
  }

  /**
   * Allinea il vincolo di "Data A" dopo ogni cambio di "Data Da": aggiorna il
   * minDate del calendario e corregge il valore già selezionato se ora sarebbe
   * antecedente.
   */
  private syncFilterDateToMinDate(): void {
    this.filterDateToFlatpickrInstance?.set('minDate', this.selectedFilterDateFrom || undefined);
    if (!this.selectedFilterDateTo) {
      return;
    }
    const clamped = this.clampFilterDateTo(this.selectedFilterDateTo);
    if (clamped.getTime() !== this.selectedFilterDateTo.getTime()) {
      this.selectedFilterDateTo = clamped;
      this.filterDateToFlatpickrInstance?.setDate(this.selectedFilterDateTo, false);
      this.notificationService.warning('"Data A" allineata a "Data Da": non può essere precedente');
    }
  }

  decrementFilterDateTo(): void {
    this.shiftFilterDateTo(-1);
  }

  incrementFilterDateTo(): void {
    this.shiftFilterDateTo(1);
  }

  /**
   * Sposta "Data A" di N giorni mantenendo l'ora a fine giornata; il
   * risultato non scende mai sotto "Data Da" (se impostata).
   */
  private shiftFilterDateTo(days: number): void {
    const base = this.selectedFilterDateTo ? new Date(this.selectedFilterDateTo) : new Date();
    base.setDate(base.getDate() + (this.selectedFilterDateTo ? days : 0));
    base.setHours(23, 59, 59, 999);

    const clamped = this.clampFilterDateTo(base);
    if (this.selectedFilterDateTo && clamped.getTime() === this.selectedFilterDateTo.getTime()) {
      // Già al minimo consentito: il pulsante non deve fare nulla.
      this.notificationService.warning('"Data A" non può essere precedente a "Data Da"');
      return;
    }

    this.selectedFilterDateTo = clamped;
    this.filterDateToFlatpickrInstance?.setDate(this.selectedFilterDateTo);
  }

  setFilterDateToToday(): void {
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    const clamped = this.clampFilterDateTo(today);
    if (clamped.getTime() !== today.getTime()) {
      this.notificationService.warning('"Data A" non può essere precedente a "Data Da"');
      return;
    }
    this.selectedFilterDateTo = today;
    this.filterDateToFlatpickrInstance?.setDate(this.selectedFilterDateTo);
  }

  clearFilterDateTo(): void {
    this.selectedFilterDateTo = null;
    this.filterDateToFlatpickrInstance?.clear();
  }

  // ===== Filtri e Azioni helpers =====
  /**
   * Pulsante "Ricerca": i filtri sono server-side, quindi rifà la query
   * ripartendo da pagina 1 (i risultati arrivano già filtrati dal backend).
   */
  searchRequests(): void {
    this.currentPage = 1;
    this.fetchPalletsPage();
  }

  toggleFiltersCollapsed(): void {
    this.isFiltersCollapsed = !this.isFiltersCollapsed;
  }

  toggleAdvancedFilters(): void {
    this.isAdvancedFiltersCollapsed = !this.isAdvancedFiltersCollapsed;
  }

  resetFilters(): void {
    this.filterPalletCode = '';
    this.filterWaybillCode = '';
    this.filterShipmentCode = '';
    this.filterDealerCode = '';
    this.filterDealerName = '';
    this.filterDealerCity = '';
    this.filterStatus = '';
    this.filterDeliveryType = '';
    this.filterWarehouse = '';
    this.filterCarrier = '';
    this.selectedFilterDateFrom = null;
    this.selectedFilterDateTo = null;
    this.filterDateFromFlatpickrInstance?.clear();
    this.filterDateToFlatpickrInstance?.clear();
    // Azzerata "Data Da", "Data A" torna senza vincolo di minimo.
    this.syncFilterDateToMinDate();
    this.applyFilters();
    // I filtri sono server-side: ricarica la prima pagina senza filtri.
    this.searchRequests();
  }

  getActiveFiltersCount(): number {
    let count = 0;
    if (this.filterPalletCode) count++;
    if (this.filterWaybillCode) count++;
    if (this.filterShipmentCode) count++;
    if (this.filterDealerCode) count++;
    if (this.filterDealerName) count++;
    if (this.filterDealerCity) count++;
    if (this.filterStatus) count++;
    if (this.filterDeliveryType) count++;
    if (this.filterWarehouse) count++;
    if (this.filterCarrier) count++;
    if (this.selectedFilterDateFrom) count++;
    if (this.selectedFilterDateTo) count++;
    return count;
  }

  setItemsPerPage(size: number): void {
    if (!this.pageSizeOptions.includes(size)) {
      return;
    }
    this.itemsPerPage = size;
    this.persistItemsPerPagePreference(size);
    this.currentPage = 1;
    this.fetchPalletsPage();
  }

  private loadItemsPerPagePreference(): void {
    try {
      const raw = localStorage.getItem(this.pageSizeStorageKey);
      if (!raw) {
        return;
      }
      const parsed = Number(raw);
      if (this.pageSizeOptions.includes(parsed)) {
        this.itemsPerPage = parsed;
      }
    } catch {
      // Ignore storage read errors and keep default page size.
    }
  }

  private persistItemsPerPagePreference(size: number): void {
    try {
      localStorage.setItem(this.pageSizeStorageKey, String(size));
    } catch {
      // Ignore storage write errors.
    }
  }

  /**
   * Cambio del filtro azienda: essendo i codici azienda dei dealerCode,
   * il filtro viene applicato lato backend rifacendo la richiesta con i
   * dealerCodes ristretti alla selezione. Riparte dalla pagina 1.
   */
  onCompanyFilterChange(): void {
    this.currentPage = 1;
    this.fetchPalletsPage();
  }

  onPageChange(page: number): void {
    if (page < 1 || page > this.getTotalPages() || page === this.currentPage) {
      return;
    }
    this.currentPage = page;
    this.fetchPalletsPage();
  }

  getTotalPages(): number {
    return Math.max(1, Math.ceil(this.totalCount / this.itemsPerPage));
  }

  getPageNumbers(): number[] {
    const totalPages = this.getTotalPages();
    const current = this.currentPage;
    const maxButtons = 5;
    let start = Math.max(1, current - Math.floor(maxButtons / 2));
    let end = Math.min(totalPages, start + maxButtons - 1);
    if (end - start + 1 < maxButtons) {
      start = Math.max(1, end - maxButtons + 1);
    }
    const pages: number[] = [];
    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
    return pages;
  }

  trackByPageNumber(_index: number, page: number): number {
    return page;
  }

  /**
   * Il reso è eliminabile solo nello stesso giorno della bolla:
   * se la data della richiesta e quella odierna differiscono anche solo
   * di un giorno, il pulsante Elimina viene disabilitato.
   */
  canDeleteRequest(r: ReturnRequest): boolean {
    if (!r.palletDate) {
      return false;
    }
    const requestDate = new Date(r.palletDate);
    if (isNaN(requestDate.getTime())) {
      return false;
    }
    const today = new Date();
    return requestDate.getFullYear() === today.getFullYear()
      && requestDate.getMonth() === today.getMonth()
      && requestDate.getDate() === today.getDate();
  }

  /**
   * Elimina il reso chiamando POST /return-request/delete: la riga viene
   * rimossa dalla tabella solo dopo la conferma del backend. Il server
   * rifiuta con 409 se palletDate non è la giornata odierna.
   */
  deleteRequest(id: number): void {
    const request = this.returnRequests.find(r => r.id === id);
    if (!request) {
      return;
    }

    const clientBrands = this.extractClientBrands() as ClientBrandsEntry[] | undefined;
    this.returnRequestService.deletePallet({
      palletCode: request.palletCode,
      palletWaybillCode: request.palletWaybillCode || '',
      palletDealerCode: request.palletDealerCode,
      palletShipmentCode: request.palletShipmentCode || ''
    }, clientBrands).subscribe({
      next: () => {
        this.returnRequests = this.returnRequests.filter(r => r.id !== id);
        this.totalCount = Math.max(0, this.totalCount - 1);
        this.applyFilters();
        this.notificationService.success(`Reso ${request.palletCode} eliminato`);
      },
      error: (err) => {
        console.error('Errore eliminazione pallet:', err);
        if (err?.status === 409) {
          this.notificationService.warning('Il reso è eliminabile solo il giorno della bolla');
        } else if (err?.status === 404) {
          this.notificationService.error('Reso non trovato');
        } else {
          this.notificationService.error('Errore nell\'eliminazione del reso');
        }
      }
    });
  }

  /** Copia il testo negli appunti (stesso pattern di waybill-detail-popup). */
  copyToClipboard(text: string): void {
    if (!text) {
      return;
    }
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(text).catch(err => {
        console.error('Failed to copy text: ', err);
      });
    } else {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      try {
        document.execCommand('copy');
      } catch (err) {
        console.error('Fallback: Unable to copy', err);
      }
      document.body.removeChild(textArea);
    }
  }

  /**
   * Codice bolla mostrato in tabella: il codice esterno del cliente
   * (palletWaybillCodeExternal) se valorizzato, altrimenti quello
   * interno generato dal server (palletWaybillCode).
   */
  getDisplayWaybillCode(r: Pallets): string {
    const external = (r.palletWaybillCodeExternal || '').trim();
    return external || r.palletWaybillCode || '';
  }

  getStatusBadgeClass(status: string): string {
    switch (status) {
      // PICKED_UP e DELIVERED sono entrambi 'RITIRATO': stesso badge.
      case 'DELIVERED': return 'rr-badge--success';
      case 'PICKED_UP': return 'rr-badge--success';
      case 'IN_TRANSIT': return 'rr-badge--info';
      case 'CONFIRMED': return 'rr-badge--warning';
      case 'REQUESTED': return 'rr-badge--pending';
      case 'REQUEST_RETURN': return 'rr-badge--pending';
      default: return 'rr-badge--neutral';
    }
  }

  /** Etichetta italiana dello stato pallet mostrata in tabella. */
  getStatusLabel(status: string): string {
    switch (status) {
      case 'PICKED_UP': return 'RITIRATO';
      case 'REQUEST_RETURN': return 'RICHIESTO';
      case 'DELIVERED': return 'RITIRATO';
      default: return status;
    }
  }
}
